import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import InputSearchWithSidebar from '../../../common/input-search-with-sidebar/input-search-with-sidebar.component';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../services/services-subsection/services-subsection.styles';
import { ParcelDetailsSearchInputsContainer } from '../../../routes/parcel-details/parcel-details.styles';
import { useStore } from '../../../stores/store';
import {
  getCityFromPlaceDetails,
  getPredictionsWithUserInput,
  getRegionFromPlaceDetails,
  getZipCodeFromPlaceDetails,
} from '../../../utils/google-places/google-places.utils';
import {
  getShipmentCountriesByFilter,
  getShipmentCountryName,
  getZipCodeDisplayValue,
} from '../../../utils/parcel-creation/parcel-creation-utils';
import { PLACES_API_MINIMAL_REQUEST_LENGTH } from '../../../stores/placesSearchStore';
import {
  VALIDATION_RULE_LATIN_WITH_COMMA,
  VALIDATION_RULE_ONLY_LETTERS,
} from '../../../constants/validation';
import { getSettingReceiverCountry } from '../../../utils/countries/countries.utils';
import { ReactComponent as ArrowRightIcon } from '../../../assets/arrow-right-icon.svg';
import { getPostalCodeLabel } from '../delivery-details-form/delivery-details-form.component';
import { testIds } from '../../../../test-ids';
import { CountryDetails } from '../../../models/shipmentCountryInfo';
import { useModal } from '../../../common/modal/use-modal.hook';
import { Modals } from '../../../constants/modals';

const ParcelDetailsDestination = () => {
  const {
    parcelCreationStore: {
      countryDeparture,
      countryDestination,
      setDestinationShipmentCountry,
      getDeliveryLimitations,
      isLoadingShipmentApi,
      isNextButtonClicked,
      omitLimitationsRequest,
      setOmitLimitationsRequest,
      prefillInProgress,
      resetShipmentDataInCaseDeliveryDetailsChanged,
      isDomestic,
      routeNotFoundError,
      isRateSchemaChanged,
    },
    shipmentCountriesStore: {
      isDirectionsListApiLoading,
      receiverPinnedList,
      receiverCountries,
    },
    placesSearchStore: { getAutocompletePredictions, getPlaceDetails },
    localizationsStore: { isLanguageChanging },
    commonStore: { isGuestParcelCreation },
  } = useStore();

  const { t } = useTranslation();
  const { openModal } = useModal();

  const showSkeleton =
    isLoadingShipmentApi ||
    isLanguageChanging ||
    prefillInProgress ||
    isDirectionsListApiLoading;

  const receiverCountrySetting = getSettingReceiverCountry(
    countryDeparture?.code,
    isDomestic
  );

  const isDefaultFlow = receiverCountrySetting === 'default_flow';

  useEffect(() => {
    if (omitLimitationsRequest || prefillInProgress) {
      return;
    }
    const isDestinationZipRequired = receiverCountrySetting !== 'without_zip';

    const isCountryDepartureValid =
      countryDeparture?.code && countryDeparture?.zipCode;
    const isCountryDestinationValid =
      countryDestination?.code &&
      (isDestinationZipRequired ? countryDestination.zipCode : true);

    if (isCountryDepartureValid && isCountryDestinationValid) {
      getDeliveryLimitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryDeparture,
    countryDestination,
    getDeliveryLimitations,
    receiverCountrySetting,
  ]);

  useEffect(() => {
    if (!routeNotFoundError && !isRateSchemaChanged) return;

    openModal({
      id: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
      name: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
      props: {
        title: routeNotFoundError?.title,
        body: routeNotFoundError?.message,
        showNotifyButton: true,
      },
    });
  }, [routeNotFoundError, isRateSchemaChanged, openModal]);

  const pinnedCountries: CountryDetails[] = useMemo(() => {
    const pinnedCodes =
      receiverPinnedList.find((x) => x.country === countryDeparture?.code)
        ?.pinned ?? [];

    const foundPinnedCountries: CountryDetails[] = pinnedCodes
      .map((x) => receiverCountries.find((y) => y.code === x))
      ?.filter((x): x is CountryDetails => !!x);

    return foundPinnedCountries;
  }, [receiverPinnedList, countryDeparture?.code, receiverCountries]);

  // TODO: Temp fix te prevent fradulent actions for US -> India guest flow
  const excludeIndia = isGuestParcelCreation && countryDeparture?.code === 'US';

  const restCountries = useMemo(() => {
    const res =
      receiverCountries.filter(
        (x) => !pinnedCountries.map((y) => y.code).includes(x.code)
      ) ?? [];

    if (excludeIndia) {
      return res.filter((c) => c.code !== 'IN');
    }

    return res;
  }, [pinnedCountries, receiverCountries, excludeIndia]);

  return (
    <ServicesSubSectionContainer>
      <ServicesSubSectionHeader>{t('to')}</ServicesSubSectionHeader>
      <ParcelDetailsSearchInputsContainer>
        <InputSearchWithSidebar<CountryDetails>
          disabled={!countryDeparture}
          name='destination_country_search'
          inputValue={getShipmentCountryName(countryDestination)}
          defaultOption={countryDestination}
          placeholder=''
          label={t('country')}
          sidebarTitle={t('country')}
          sidebarInputPlaceholder={t('start_typing_country_name')}
          displayAllOptionsWithEmptyFilter
          isOptionSelected={(a) => a?.code === countryDestination?.code}
          getDefaultSearchOptions={(filter) =>
            Promise.resolve(
              getShipmentCountriesByFilter(
                pinnedCountries,
                filter,
                undefined,
                true
              )
            )
          }
          getSearchOptions={(filter) =>
            Promise.resolve(getShipmentCountriesByFilter(restCountries, filter))
          }
          getKeyForSearchOption={(country) => country.code}
          getDisplayValueForSearchOption={(country) =>
            getShipmentCountryName(country)
          }
          onSearchOptionSelected={(country) => {
            setDestinationShipmentCountry(country);
            setOmitLimitationsRequest(false);
            resetShipmentDataInCaseDeliveryDetailsChanged();
          }}
          errorMessage={t('this_field_cannot_be_empty')}
          mainInputValidationPredicate={(inputValue) => !!inputValue}
          showSkeleton={showSkeleton}
          validationRule={VALIDATION_RULE_ONLY_LETTERS}
          triggerValidation={isNextButtonClicked.value}
          inputIcon={ArrowRightIcon}
          infoMessage={isDomestic ? t('domestic_selected_info') : undefined}
          inputTestId={
            testIds.parceCreation.parcelDetails.destination.countryInput
          }
        />

        {isDefaultFlow && (
          <InputSearchWithSidebar<google.maps.places.AutocompletePrediction>
            disabled={!countryDestination}
            inputValue={getZipCodeDisplayValue(countryDestination)}
            name='destination_zip_search'
            placeholder=''
            label={getPostalCodeLabel(countryDestination?.code)}
            sidebarTitle={getPostalCodeLabel(countryDestination?.code)}
            sidebarInputPlaceholder={t('start_typing_zip-code')}
            debounceSidebarInputChange
            shouldValidateSearchQuery
            getSearchOptions={async (filter) => {
              const predictions = await getAutocompletePredictions({
                value: filter ?? '',
                predictionTypes: ['geocode'],
                countryConstraint: countryDestination?.code.toLocaleLowerCase(),
              });

              return getPredictionsWithUserInput(
                predictions,
                filter,
                PLACES_API_MINIMAL_REQUEST_LENGTH
              );
            }}
            getKeyForSearchOption={(place) => place.place_id}
            getDisplayValueForSearchOption={(place) => place.description}
            onSearchOptionSelected={async (place) => {
              setOmitLimitationsRequest(false);
              if (place.place_id && place.place_id !== place.description) {
                const placeDetails = await getPlaceDetails(
                  place,
                  undefined,
                  undefined,
                  true
                );
                if (!placeDetails || !countryDestination) return;
                const zipCode = getZipCodeFromPlaceDetails(
                  placeDetails.address_components
                );
                const city = getCityFromPlaceDetails(
                  placeDetails.address_components,
                  countryDestination.code.toLocaleLowerCase()
                );

                const region = await getRegionFromPlaceDetails(
                  placeDetails.address_components,
                  countryDestination.code.toLocaleLowerCase()
                );

                setDestinationShipmentCountry({
                  ...countryDestination,
                  zipCode: zipCode || place.description,
                  region,
                  city,
                });
              } else {
                if (!countryDestination) return;
                setDestinationShipmentCountry({
                  ...countryDestination,
                  zipCode: place.description,
                  city: undefined,
                });
              }
              resetShipmentDataInCaseDeliveryDetailsChanged();
            }}
            canModifyInput={false}
            onInputChange={(inputValue) => {
              const zipCode = inputValue;

              countryDestination &&
                setDestinationShipmentCountry({
                  ...countryDestination,
                  zipCode,
                  city: undefined,
                });
            }}
            errorMessage={t('this_field_cannot_be_empty')}
            mainInputValidationPredicate={(inputValue) => !!inputValue}
            showSkeleton={showSkeleton}
            validationRule={VALIDATION_RULE_LATIN_WITH_COMMA}
            triggerValidation={isNextButtonClicked.value}
            inputIcon={ArrowRightIcon}
            inputTestId={
              testIds.parceCreation.parcelDetails.destination.postCodeInput
            }
          />
        )}
      </ParcelDetailsSearchInputsContainer>
    </ServicesSubSectionContainer>
  );
};

export default observer(ParcelDetailsDestination);
